import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Layers, Check, ChevronDown, X, Building2 } from "lucide-react";
import { useCompanies } from "@/context/CompanyContext";

/**
 * Sections whose contents are owned by a company. The scope filter only appears
 * on these.
 *
 * Clients are deliberately absent: they are shared across every company on the
 * account, so a company filter there would either do nothing or quietly hide
 * clients the user can still bill. Templates, Companies, Settings and the admin
 * screens are account-level for the same reason. Showing a control that has no
 * effect is worse than not showing it.
 */
const SCOPED_SECTIONS = ["", "invoices", "receipts", "purchase-orders", "notes"];

export default function CompanyScopeFilter() {
  const location = useLocation();
  const {
    companies,
    loading,
    scopeMode,
    scopeIds,
    scopeIsAll,
    scopeLabel,
    setAllScope,
    setActiveScope,
    toggleScopeCompany,
  } = useCompanies();

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const section = (location.pathname.split("/")[2] || "").split("/")[0];
  const applies = SCOPED_SECTIONS.includes(section);

  // Navigating to a page the control does not apply to must not leave its menu
  // hanging open over unrelated content.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // With one profile there is nothing to choose between: the scope is always
  // that company, so a control with a single entry would only add noise.
  if (loading) return null;
  if (companies.length < 2) return null;
  if (!applies) return null;

  const selected = scopeIsAll ? companies.map((c) => c.id) : scopeIds;
  const isPicked = (id) => selected.includes(id);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-2.5 h-9 border border-[var(--border)] rounded-md bg-white hover:bg-gray-50 text-sm max-w-[240px]"
        data-testid="company-scope"
        aria-haspopup="true"
        aria-expanded={open}
        title="Choose which companies this view covers"
      >
        <Layers className="w-4 h-4 shrink-0 text-[var(--primary)]" />
        <span className="truncate font-medium" data-testid="company-scope-label">
          {scopeLabel}
        </span>
        <span className="text-[11px] text-gray-400 num shrink-0 hidden sm:inline">
          {scopeIsAll ? companies.length : selected.length}/{companies.length}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-80 bg-white border border-[var(--border)] rounded-lg shadow-lg overflow-hidden"
          data-testid="company-scope-menu"
        >
          <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between">
            <span className="overline">Show documents for</span>
            <span className="text-[11px] text-gray-500 num">
              {selected.length} of {companies.length}
            </span>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {/* Whole-account scope. */}
            <label
              className="px-3 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 cursor-pointer border-b border-[var(--border)]"
              data-testid="company-scope-all"
            >
              <input
                type="checkbox"
                checked={scopeIsAll}
                onChange={(e) => (e.target.checked ? setAllScope() : setActiveScope())}
                className="w-4 h-4 accent-[var(--primary)] shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">All companies</span>
                <span className="block text-[11px] text-gray-500">
                  Invoices, receipts and totals across every company
                </span>
              </span>
            </label>

            {/* Active company only. */}
            <label
              className="px-3 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 cursor-pointer border-b border-[var(--border)]"
              data-testid="company-scope-active"
            >
              <input
                type="checkbox"
                checked={scopeMode === "active"}
                onChange={() => setActiveScope()}
                className="w-4 h-4 accent-[var(--primary)] shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">Active company only</span>
                <span className="block text-[11px] text-gray-500">
                  Follows the company picked in the top-right switcher
                </span>
              </span>
            </label>

            <div className="px-3 pt-2 pb-1 overline">Or pick specific companies</div>

            {companies.map((c) => (
              <label
                key={c.id}
                className="px-3 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 cursor-pointer border-b border-[var(--border)] last:border-0"
                data-testid={`company-scope-option-${c.id}`}
              >
                <input
                  type="checkbox"
                  checked={isPicked(c.id)}
                  onChange={() => toggleScopeCompany(c.id)}
                  className="w-4 h-4 accent-[var(--primary)] shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium truncate">
                    {c.name || "Unnamed company"}
                    {c.is_default ? " (active)" : ""}
                  </span>
                  <span className="block text-[11px] text-gray-500 truncate">
                    {c.label ? `${c.label} · ` : ""}
                    {c.gstin || "No GSTIN"}
                    {typeof c.doc_count === "number" ? ` · ${c.doc_count} docs` : ""}
                  </span>
                </span>
                {isPicked(c.id) && <Check className="w-3.5 h-3.5 text-[var(--primary)] shrink-0" />}
              </label>
            ))}
          </div>

          {selected.length > 1 && (
            <div className="p-2 border-t border-[var(--border)] bg-gray-50/60 flex items-center justify-between gap-2">
              <span className="text-[11px] text-gray-500 flex items-center gap-1.5 min-w-0">
                <Building2 className="w-3 h-3 shrink-0" />
                <span className="truncate">
                  {scopeIsAll ? "Showing every company" : `${selected.length} selected`}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setActiveScope()}
                className="text-xs px-2.5 h-7 leading-7 border border-[var(--border)] rounded-md bg-white hover:bg-gray-50 shrink-0 inline-flex items-center gap-1"
                data-testid="company-scope-clear"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}